import React, { useMemo, useState } from "react";
import DashboardWidgetCard from "./components/DashboardWidgetCard";
import DashboardCardHeader from "./components/DashboardCardHeader";
import { makeStyles } from "@material-ui/core";
import MapRegion from "./icons/MapRegion.svg";
import MapArea from "./icons/MapArea.svg";
import MapGpsTerritory from "./icons/MapGpsTerritory.svg";
import WorkOfficeBuilding from "./icons/WorkOfficeBuilding.svg";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CustomSelect from "./components/CustomSelect";
import useNoParamFetch from "./hooks/useNoParamFetch";
import BounceLoader from "react-spinners/BounceLoader";
import useOnlyParamFetch from "./hooks/useOnlyParamFetch";
import GreenTickIcon from "./icons/green_tick.svg";
import DatePicker from "./components/datePicker";

const callOutcomeOptions = [
  { id: "Existing Merchant Served", name: "Existing Merchant Served" },
  { id: "Lead Generated", name: "Lead Generated" },
  { id: "Lead Followed-up", name: "Lead Followed-up" },
  { id: "Lead Closed", name: "Lead Closed" },
];

const qrStatusOptions = [
  { id: "matched", name: "Matched" },
  { id: "mismatched", name: "Mismatched" },
  { id: "invalid", name: "Invalid" },
  { id: "not_found", name: "Not Found" },
];

const outletStatusOptions = [
  { id: "open", name: "Open" },
  { id: "closed", name: "Closed" },
  { id: "not_found", name: "Not Found" },
];

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: "white",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "rgba(54, 43, 115, 1)",
    margin: theme.spacing(0, 1.5),
    "& h1": {
      fontSize: 24,
      fontWeight: 700,
      margin: 0,
    },
    "& p": {
      fontSize: 14,
      fontWeight: 500,
      margin: 0,
    },
  },
  filtersContent: {
    display: "flex",
    backgroundColor: "rgba(248, 249, 252, 1)",
    borderRadius: 8,
    padding: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("md")]: {
      flexWrap: "wrap",
    },
  },
  headerActions: {
    display: "flex",
    color: "rgba(226, 19, 110, 1)",
    "& p": {
      margin: 0,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
    },
  },
  separatorLine: {
    margin: theme.spacing(0, 2),
    border: "0.5px solid rgba(0, 14, 61, 0.08)",
  },
  greenTick: {
    width: 20,
    marginRight: 5,
  },
  filterWrapper: {
    minWidth: "20%",
    [theme.breakpoints.down("md")]: {
      width: "50%",
    },
  },
}));

export default function GeographyFilters({
  agency,
  clusters,
  region,
  area,
  territory,
  field_force_id,
  team,
  onFilterChange,
  includeDatePicker,
  dateRange,
  onChangeDateRange,
  label,
  subLabel,
  isCallFilter = false,
  call_outcome,
  qr_status,
  outlet_status,
}) {
  const [clusterLocal, setClusterLocal] = useState(clusters);
  const [agencyLocal, setAgencyLocal] = useState(agency);
  const [regionLocal, setRegionLocal] = useState(region);
  const [areaLocal, setAreaLocal] = useState(area);
  const [territoryLocal, setTerritoryLocal] = useState(territory);
  const [fieldForceLocal, setFieldForceLocal] = useState(field_force_id);
  const [teamLocal, setTeamLocal] = useState(team);
  const [callOutcomeLocal, setCallOutcomeLocal] = useState(call_outcome);
  const [qrStatusLocal, setQrStatusLocal] = useState(qr_status);
  const [outletStatusLocal, setOutletStatusLocal] = useState(outlet_status);
  const [isFilterApplied, setFilterApply] = useState(true);

  const clusterParams = useMemo(() => {
    return clusterLocal?.length > 0 ? { clusters: clusterLocal } : null;
  }, [clusterLocal]);
  const regionParams = useMemo(() => {
    return regionLocal?.length > 0 ? { region: regionLocal } : null;
  }, [regionLocal]);

  const areaParams = useMemo(() => {
    return areaLocal?.length > 0 ? { area: areaLocal } : null;
  }, [areaLocal]);

  const territoryParams = useMemo(() => {
    return territoryLocal?.length > 0 ? { territory: territoryLocal } : null;
  }, [territoryLocal]);

  const agencyParams = useMemo(() => {
    return agencyLocal?.length > 0 ? { agency: agencyLocal } : null;
  }, [agencyLocal]);

  const teamParams = useMemo(() => {
    var params = "";
    teamLocal.forEach((t) => {
      params += `&mdo_type=${t}`;
    });
    return params;
  }, [teamLocal]);

  const { data: clusterData } = useNoParamFetch(
    "/organizations/api/v1/clusters"
  );
  const { data: regionData } = useOnlyParamFetch(
    "/organizations/api/v1/regions",
    clusterParams
  );
  const { data: areaData } = useOnlyParamFetch(
    "/organizations/api/v1/areas",
    regionParams ? regionParams : clusterParams
  );
  const { data: territoriesData } = useOnlyParamFetch(
    "/organizations/api/v1/territories",
    areaParams ? areaParams : regionParams ? regionParams : clusterParams
  );

  const { data: agencyData } = useOnlyParamFetch(
    "/organizations/api/v1/agency",
    territoryParams
      ? territoryParams
      : areaParams
        ? areaParams
        : regionParams
          ? regionParams
          : clusterParams
  );

  const { data: filedForceData } = useOnlyParamFetch(
    `/admin_panel/v1/mdo-list?page_index=0&page_size=10000&filter=yes${teamParams}`,
    agencyParams
      ? agencyParams
      : territoryParams
        ? territoryParams
        : areaParams
          ? areaParams
          : regionParams
            ? regionParams
            : clusterParams
  );
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  const classes = useStyles();

  const clearFilters = () => {
    onFilterChange({
      clusters: [],
      agency: [],
      region: [],
      area: [],
      territory: [],
      field_force_id: [],
      team: [],
      call_outcome: [],
      qr_status: [],
      outlet_status: [],
    });
    setClusterLocal([]);
    setAgencyLocal([]);
    setAreaLocal([]);
    setRegionLocal([]);
    setTerritoryLocal([]);
    setFieldForceLocal([]);
    setTeamLocal([]);
    setCallOutcomeLocal([]);
    setQrStatusLocal([]);
    setOutletStatusLocal([]);
    setFilterApply(true);
  };

  const applyFilters = () => {
    var filters = {
      clusters: clusterLocal,
      agency: agencyLocal,
      region: regionLocal,
      area: areaLocal,
      territory: territoryLocal,
      team: teamLocal,
      field_force_id: fieldForceLocal,
    };

    if (isCallFilter) {
      filters.call_outcome = callOutcomeLocal;
      filters.qr_status = qrStatusLocal;
      filters.outlet_status = outletStatusLocal;
    }
    onFilterChange(filters);
    setFilterApply(true);
  };
  return (
    <DashboardWidgetCard className={classes.card}>
      <DashboardCardHeader label={label} subLabel={subLabel}>
        <div className={classes.headerActions}>
          {includeDatePicker && (
            <DatePicker
              dateRange={dateRange}
              onChangeDateRange={onChangeDateRange}
            />
          )}
          {includeDatePicker && <div className={classes.separatorLine}></div>}
          {!isFiltersCollapsed && (
            <p onClick={isFilterApplied ? () => {} : applyFilters}>
              {false && (
                <img
                  src={GreenTickIcon}
                  className={classes.greenTick}
                  alt="green-tick"
                />
              )}
              {isFilterApplied ? `Filter Applied` : "Apply Filters"}
            </p>
          )}
          {!isFiltersCollapsed && <div className={classes.separatorLine}></div>}
          {!isFiltersCollapsed && <p onClick={clearFilters}>Clear Filters</p>}
          {!isFiltersCollapsed && <div className={classes.separatorLine}></div>}
          <p onClick={() => setIsFiltersCollapsed((prev) => !prev)}>
            {isFiltersCollapsed ? "Expand Filters" : "Collapse Filters"}
            {isFiltersCollapsed ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowUpIcon fontSize="small" />
            )}
          </p>
        </div>
      </DashboardCardHeader>
      {!isFiltersCollapsed && (
        <div className={classes.filtersContent}>
          {/* <div className={classes.filterWrapper}>
            {teamData ? (
              <CustomSelect
                item={{
                  label: "Field-Force Type",
                  key: "team",
                  options: teamData,
                  value: teamLocal,
                  category: "geo_filter",
                  icon: WorkOfficeBuilding,
                }}
                handleChange={(e) => {
                  setTeamLocal(e.target.value);
                  setFilterApply(false);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div> */}
          <div className={classes.filterWrapper}>
            {true ? (
              <CustomSelect
                item={{
                  label: "Circle",
                  key: "clusters",
                  options: clusterData,
                  value: clusterLocal,
                  category: "geo_filter",
                  icon: MapRegion,
                }}
                handleChange={(e) => {
                  setClusterLocal(e.target.value);
                  setRegionLocal([]);
                  setAgencyLocal([]);
                  setAreaLocal([]);
                  setTerritoryLocal([]);
                  setFieldForceLocal([]);
                  setFilterApply(false);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
          <div className={classes.filterWrapper}>
            {true ? (
              <CustomSelect
                item={{
                  label: "Zone",
                  key: "region",
                  options: regionData,
                  value: regionLocal,
                  category: "geo_filter",
                  icon: MapRegion,
                }}
                handleChange={(e) => {
                  setRegionLocal(e.target.value);
                  setAgencyLocal([]);
                  setAreaLocal([]);
                  setTerritoryLocal([]);
                  setFieldForceLocal([]);
                  setFilterApply(false);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>

          <div className={classes.filterWrapper}>
            {true ? (
              <CustomSelect
                item={{
                  label: "Area",
                  key: "area",
                  options: areaData,
                  value: areaLocal,
                  category: "geo_filter",
                  icon: MapArea,
                }}
                handleChange={(e) => {
                  setAreaLocal(e.target.value);
                  setAgencyLocal([]);
                  setTerritoryLocal([]);
                  setFieldForceLocal([]);
                  setFilterApply(false);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
          <div className={classes.filterWrapper}>
            {true ? (
              <CustomSelect
                item={{
                  label: "Territory",
                  key: "territory",
                  options: territoriesData,
                  value: territoryLocal,
                  category: "geo_filter",
                  icon: MapGpsTerritory,
                }}
                handleChange={(e) => {
                  setTerritoryLocal(e.target.value);
                  setAgencyLocal([]);
                  setFieldForceLocal([]);
                  setFilterApply(false);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
          <div className={classes.filterWrapper}>
            {true ? (
              <CustomSelect
                item={{
                  label: "Distributor",
                  key: "distributor",
                  options: agencyData,
                  value: agencyLocal,
                  category: "geo_filter",
                  icon: WorkOfficeBuilding,
                }}
                handleChange={(e) => {
                  setAgencyLocal(e.target.value);
                  setFieldForceLocal([]);
                  setFilterApply(false);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
          <div className={classes.filterWrapper}>
            {true ? (
              <CustomSelect
                item={{
                  label: "Field-Force",
                  key: "field_force_id",
                  options: filedForceData,
                  value: fieldForceLocal,
                  category: "geo_filter",
                  icon: WorkOfficeBuilding,
                }}
                handleChange={(e) => {
                  setFieldForceLocal(e.target.value);
                  setFilterApply(false);
                }}
                isMultiSelect={true}
              />
            ) : (
              <BounceLoader />
            )}
          </div>
          {isCallFilter && (
            <div className={classes.filterWrapper}>
              <CustomSelect
                item={{
                  label: "Call Outcome",
                  key: "call_outcome",
                  options: callOutcomeOptions,
                  value: callOutcomeLocal,
                  category: "call_filter",
                  icon: WorkOfficeBuilding,
                }}
                handleChange={(e) => {
                  setCallOutcomeLocal(e.target.value);
                  setFilterApply(false);
                }}
                isMultiSelect={true}
              />
            </div>
          )}
          {isCallFilter && (
            <div className={classes.filterWrapper}>
              <CustomSelect
                item={{
                  label: "QR Status",
                  key: "qr_status",
                  options: qrStatusOptions,
                  value: qrStatusLocal,
                  category: "call_filter",
                  icon: WorkOfficeBuilding,
                }}
                handleChange={(e) => {
                  setQrStatusLocal(e.target.value);
                  setFilterApply(false);
                }}
                isMultiSelect={true}
              />
            </div>
          )}
          {isCallFilter && (
            <div className={classes.filterWrapper}>
              <CustomSelect
                item={{
                  label: "Outlet Status",
                  key: "outlet_status",
                  options: outletStatusOptions,
                  value: outletStatusLocal,
                  category: "call_filter",
                  icon: WorkOfficeBuilding,
                }}
                handleChange={(e) => {
                  setOutletStatusLocal(e.target.value);
                  setFilterApply(false);
                }}
                isMultiSelect={false}
              />
            </div>
          )}
        </div>
      )}
    </DashboardWidgetCard>
  );
}
